import { z } from "zod";
import { api } from "./client";

/* ──────────────────────────────────────────────────────────────────────
   Schemas mirror api/models/masters.py
   ────────────────────────────────────────────────────────────────────── */

export const EditionRow = z.object({
  edition_id: z.number().int(),
  channel_id: z.number().int(),
  channel_name: z.string(),
  edition: z.string().nullable(),
  city: z.string().nullable(),
  city_id: z.number().int().nullable(),
  state: z.string().nullable(),
  state_id: z.number().int().nullable(),
  zone: z.string().nullable(),
  circulation: z.number().int().nullable(),
  readership: z.number().int().nullable(),
  category_id: z.number().int().nullable(),
  category: z.string().nullable(),
  language_id: z.number().int().nullable(),
  language: z.string().nullable(),
  language_iso: z.string().nullable(),
  rating: z.string().nullable(),
  serial_no: z.number().int().nullable(),
  channel_is_deleted: z.boolean().nullable(),
  is_active: z.boolean(),
  is_crawlable: z.boolean(),
  is_scraped: z.boolean(),
  has_active_crawler_config: z.boolean(),
  issues_last_30: z.number().int(),
  last_issue_date: z.string().nullable(),
  needs_review: z.boolean(),
  review_reason: z.string().nullable(),
  reviewed_at: z.string().nullable(),
  reviewed_by: z.string().nullable(),
  updated_at: z.string().nullable(),
  updated_by: z.string().nullable(),
});
export type EditionRow = z.infer<typeof EditionRow>;

export const EditionListPage = z.object({
  total: z.number().int(),
  page: z.number().int(),
  page_size: z.number().int(),
  has_more: z.boolean(),
  rows: z.array(EditionRow),
  counts: z.record(z.number().int()),
});
export type EditionListPage = z.infer<typeof EditionListPage>;

export const CategoryOption  = z.object({
  id: z.number().int(),
  name: z.string(),
  edition_count: z.number().int(),
});
export const LanguageOption  = z.object({
  id: z.number().int(),
  name: z.string(),
  iso_code: z.string().nullable(),
  edition_count: z.number().int(),
});
export const CityOption = z.object({
  city_id: z.number().int().nullable(),
  city: z.string(),
  state: z.string().nullable(),
  state_id: z.number().int().nullable(),
  zone: z.string().nullable().optional(),
  edition_count: z.number().int(),
});
export type CityOption = z.infer<typeof CityOption>;
export const StateOption = z.object({
  state_id: z.number().int().nullable(),
  state: z.string(),
  edition_count: z.number().int(),
});
export type StateOption = z.infer<typeof StateOption>;

export const Dimensions = z.object({
  categories: z.array(CategoryOption),
  languages:  z.array(LanguageOption),
  cities:     z.array(CityOption),
  states:     z.array(StateOption),
});
export type Dimensions = z.infer<typeof Dimensions>;

/* ──────────────────────────────────────────────────────────────────────
   Calls
   ────────────────────────────────────────────────────────────────────── */

export interface EditionsFilters {
  page?: number;
  pageSize?: number;
  q?: string;
  categoryId?: number;
  languageId?: number;
  isActive?: boolean;
  isCrawlable?: boolean;
  needsReview?: boolean;
  hasNulls?: boolean;
  sort?: "channel_name" | "city" | "last_issue" | "issues_last_30" | "review";
}

export async function fetchMastersDimensions(): Promise<Dimensions> {
  const r = await api.get("/api/masters/dimensions");
  return Dimensions.parse(r.data);
}

export async function fetchMastersEditions(opts: EditionsFilters = {}): Promise<EditionListPage> {
  const p = new URLSearchParams();
  if (opts.page)        p.set("page",         String(opts.page));
  if (opts.pageSize)    p.set("page_size",    String(opts.pageSize));
  if (opts.q)           p.set("q",            opts.q);
  if (opts.categoryId)  p.set("category_id",  String(opts.categoryId));
  if (opts.languageId)  p.set("language_id",  String(opts.languageId));
  if (opts.isActive    !== undefined) p.set("is_active",    String(opts.isActive));
  if (opts.isCrawlable !== undefined) p.set("is_crawlable", String(opts.isCrawlable));
  if (opts.needsReview !== undefined) p.set("needs_review", String(opts.needsReview));
  if (opts.hasNulls    !== undefined) p.set("has_nulls",    String(opts.hasNulls));
  if (opts.sort)        p.set("sort",         opts.sort);
  const qs = p.toString();
  const r = await api.get(`/api/masters/editions${qs ? `?${qs}` : ""}`);
  return EditionListPage.parse(r.data);
}

export interface EditionPatchBody {
  city?: string;
  city_id?: number | null;
  edition?: string;
  state?: string;
  state_id?: number | null;
  zone?: string;
  is_active?: boolean;
  is_crawlable?: boolean;
  is_scraped?: boolean;
  circulation?: number | null;
  readership?: number | null;
  review_action?: "keep" | "clear";
}

export async function patchEdition(editionId: number, body: EditionPatchBody): Promise<EditionRow> {
  const r = await api.patch(`/api/masters/editions/${editionId}`, body);
  return EditionRow.parse(r.data);
}

export interface ChannelPatchBody {
  channel_name?: string;
  category_id?: number;
  language_id?: number;
  is_deleted?: boolean;
  review_action?: "keep" | "clear";
}

export async function patchChannel(channelId: number, body: ChannelPatchBody): Promise<unknown> {
  const r = await api.patch(`/api/masters/channels/${channelId}`, body);
  return r.data;
}

/* ── Channel preview for rename-confirmation modal ─────────────────── */
export const ChannelEditionLite = z.object({
  edition_id: z.number().int(),
  city: z.string().nullable(),
  edition: z.string().nullable(),
  is_active: z.boolean(),
});
export type ChannelEditionLite = z.infer<typeof ChannelEditionLite>;

export const ChannelPreview = z.object({
  channel_id: z.number().int(),
  channel_name: z.string(),
  category_id: z.number().int().nullable(),
  category: z.string().nullable(),
  language_id: z.number().int().nullable(),
  language: z.string().nullable(),
  language_iso: z.string().nullable(),
  is_deleted: z.boolean(),
  edition_count: z.number().int(),
  editions: z.array(ChannelEditionLite),
});
export type ChannelPreview = z.infer<typeof ChannelPreview>;

export async function fetchChannelPreview(channelId: number): Promise<ChannelPreview> {
  const r = await api.get(`/api/masters/channels/${channelId}`);
  return ChannelPreview.parse(r.data);
}
